import { useRef, useState } from "react"
import { FaCircleCheck } from "react-icons/fa6"
import { useNavigate } from "react-router-dom";
import { Box, Center, HStack, Link, Separator, Spacer, Stack, VStack } from "@chakra-ui/react"
import { Button, Text, TextField } from "@/components"
import { NdaDocumentRequirements } from "./NdaDocumentRequirements"
import { useAuth } from "@/contexts"
import { useInsertNda } from "@/hooks"

interface Props {
  isInitialScreen: boolean
}

export const NdaFormComponent = ({ isInitialScreen }: Props) => {
  const formRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate();
  const { user } = useAuth()
  const { mutateAsync: insertNdaInfo, isPending, isSuccess } = useInsertNda()

  const [firstName, setFirstName] = useState<string>("")
  const [middleName, setMiddleName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [suffix, setSuffix] = useState<string>("")
  const [preferredEmail, setPreferredEmail] = useState<string>("")
  const [address, setAddress] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [state, setState] = useState<string>("")
  const [zipCode, setZipCode] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")

  if (!user) return null

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const handleSubmitClick = async ({
    firstName,
    middleName,
    lastName,
    preferredEmail,
    suffix,
    address,
    city,
    state,
    zipCode,
    phoneNumber
  }: {
    firstName: string,
    middleName: string,
    lastName: string,
    preferredEmail: string,
    suffix: string,
    address: string,
    city: string,
    state: string,
    zipCode: string,
    phoneNumber: string
  }) => {
    if (user.userType !== "client") return
    try {
      if (firstName === "" ||
        middleName === "" || 
        lastName === "" || 
        preferredEmail === "" || 
        address === "" ||
        city === "" || 
        state === "" || 
        zipCode === "" || 
        phoneNumber === ""
      ) {
        alert("Please fill out all required fields.")
        return
      }

      await insertNdaInfo({
        legalName: [firstName, middleName, lastName, suffix].filter(Boolean).join(" "),
        email: preferredEmail,
        address,
        city,
        state,
        zip: zipCode,
        phone: phoneNumber,
      })
    } catch (error) {
      console.error("Error inserting NDA information:", error)
    }
  }

  if (isSuccess) {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w="40%" p={5} borderRadius={10} bg="card.bg" boxShadow="md" mt={20} >
          <Box borderRadius={5} bg="green.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center" mt={2} mb={2}>
            Thank You!
          </Text>
          <Text fontSize="sm" textAlign="center" mb={5}>
            Thank you for signing our Client Information Form. Please check your email for a DocuSign link sent from docusign@esofund.com which you and your Equity Partner will sign.
          </Text>
          <Text fontSize="sm" textAlign="center">If you didn't receive an email please contact your Equity Partner.</Text>
          <Button variant="solid" onClick={() => navigate("/client/documents")}>Proceed to the Portal</Button>
        </VStack>
      </Center>
    )
  }

  return (
    <Stack w={isInitialScreen ? "60%" : "100%"} h="100vh" bg="card.bg" p={10} borderLeftRadius={20} overflowY="auto">
      <Text fontSize="2xl" fontWeight="bold" fontFamily="body" mb={5}>
        Welcome! We're excited to work with you!
      </Text>
      <Text fontSize="md" mb={5}>
        To get started, please fill out the{" "}
      <Text
        as="span"
        color="blue.500"
        fontWeight="bold"
        cursor="pointer"
        _hover={{ textDecoration: "underline" }}
        onClick={scrollToForm}
      >
        form below
      </Text>{" "}with your details. Once completed you will receive a DocuSign link to sign the Mutual Non-Disclosure Agreement (MNDA).
      </Text>
      <Text>
        This MNDA protects you and any information shared between you and ESO Fund. It also prohibits the sharing of ESO Fund materials with anyone other than the professional advisors assisting you on this transaction.
      </Text>

      <Text mt={5}>
        After signing, please send the following items, ensuring that both the{" "}
        <Text as="span" fontWeight="bold">company name</Text> and{" "}
        <Text as="span" fontWeight="bold">current date</Text> are visible:
      </Text>

      <NdaDocumentRequirements />

      <Text>
        For an overview of our funding process, please go to our{" "}
        <Text as={Link} color="blue.500" fontWeight="bold" onClick={() => window.open("https://esofund.com/overview", "_blank")}>
          website
        </Text>.
      </Text>
      <Separator mt={5} mb={5} />
      <Stack gap={10} ref={formRef}>
        <HStack gap={4} mt={4}>
          <TextField
            label="First Name"
            placeholder="Enter your first name"
            type="text"
            value={firstName}
            required
            onChange={(e) => setFirstName(e)}
          />
          <TextField
            label="Middle Name"
            placeholder="Enter your middle name"
            value={middleName}
            onChange={(e) => setMiddleName(e)}
          />
        </HStack>
        <HStack gap={4}>
          <TextField
            label="Last Name"
            placeholder="Enter your last name"
            type="text"
            value={lastName}
            required
            onChange={(e) => setLastName(e)}
          />
          <TextField
            label="Suffix"
            placeholder="Enter your suffix"
            value={suffix}
            onChange={(e) => setSuffix(e)}
          />
        </HStack>

        <HStack gap={4}>
          <TextField
            label="Preferred Email"
            placeholder="Enter your preferred email"
            type="email"
            value={preferredEmail}
            required
            onChange={(e) => setPreferredEmail(e)}
          />
          <TextField
            label="Phone Number"
            placeholder="Enter your phone number"
            type="phone"
            value={phoneNumber}
            required
            onChange={(e) => setPhoneNumber(e)}
          />
        </HStack>

        <TextField
          label="Address"
          placeholder="Enter your address"
          type="text"
          value={address}
          required
          onChange={(e) => setAddress(e)}
        />

        <TextField
          label="City"
          placeholder="Enter your city"
          type="text"
          value={city}
          required
          onChange={(e) => setCity(e)}
        />

        <HStack gap={4}>
          <TextField
            label="State"
            placeholder="Enter your state"
            type="text"
            value={state}
            required
            onChange={(e) => setState(e)}
          />
          <TextField
            label="Zip Code"
            placeholder="Enter your zip code"
            type="text"
            value={zipCode}
            required
            onChange={(e) => setZipCode(e)}
          />
        </HStack>
      </Stack>
      <HStack>
        <Spacer />
        <Button
          variant="solid"
          w="fit-content"
          mt={5}
          loading={isPending}
          onClick={() => handleSubmitClick({
            firstName,
            middleName,
            lastName,
            preferredEmail,
            suffix,
            address,
            city,
            state,
            zipCode,
            phoneNumber
          })}
        >
          Submit
        </Button>
      </HStack>
    </Stack>
  )
}
